<template>
  <jet-form-section @submitted="updateProfileInformation">
    <template #title>
      {{ __('Profile Information') }}
    </template>

    <template #description>
      {{ __('Update your account\'s profile information and email address.') }}
    </template>

    <template #form>
      <!-- Profile Photo -->
      <div class="col-span-full" v-if="$page.props.jetstream.managesProfilePhotos">
        <!-- Profile Photo File Input -->
        <input type="file" class="hidden"
               ref="photo"
               @change="updatePhotoPreview">

        <jet-label class="mb-2" for="photo" :value="__('Photo')"/>
        <div class="flex items-center space-x-4">
          <div>
            <!-- Current Profile Photo -->
            <div v-show="! photoPreview">
              <img :src="user.profile_photo_url" :alt="user.name" class="rounded-md h-20 w-20 object-cover">
            </div>

            <!-- New Profile Photo Preview -->
            <div v-show="photoPreview">
            <span class="block rounded-md w-20 h-20 bg-cover bg-no-repeat bg-center"
                  :style="'background-image: url(\'' + photoPreview + '\');'">
            </span>
            </div>

            <jet-input-error :message="form.errors.photo" class="mt-2"/>
          </div>
          <jet-secondary-button type="button" @click.prevent="selectNewPhoto">
            {{ __('Select A New Photo') }}
          </jet-secondary-button>
        </div>

<!--        <jet-secondary-button type="button" class="mt-2" @click.prevent="deletePhoto" v-if="user.profile_photo_path">-->
<!--          {{ __('Remove Photo') }}-->
<!--        </jet-secondary-button>-->
      </div>

      <!-- Name -->
      <div class="col-span-2">
        <jet-label for="name" :value="__('Name')"/>
        <jet-input id="name" type="text" class="mt-1 block w-full" v-model="form.name" autocomplete="name"/>
        <jet-input-error :message="form.errors.name" class="mt-2"/>
      </div>

      <!-- Email -->
      <div class="col-span-4">
        <jet-label for="email" :value="__('E-Mail')"/>
        <jet-input id="email" type="email" class="mt-1 block w-full" v-model="form.email"/>
        <jet-input-error :message="form.errors.email" class="mt-2"/>
      </div>

      <div class="col-span-2">
        <jet-label for="first-name" :value="__('First name')"/>
        <jet-input id="first-name" type="text" name="first-name" class="mt-1 block w-full" autocomplete="given-name" v-model="form.firstname"/>
        <jet-input-error :message="form.errors.firstname" class="mt-2"/>
      </div>

      <div class="col-span-2">
        <jet-label for="last-name" :value="__('Last name')"/>
        <jet-input id="last-name" type="text" name="last-name" class="mt-1 block w-full" autocomplete="family-name" v-model="form.lastname"/>
        <jet-input-error :message="form.errors.lastname" class="mt-2"/>
      </div>

      <div class="col-span-2">
        <jet-label for="country" :value="__('Country')"/>
        <!-- @translations {{ __('Germany') }}, {{ __('Austria') }}, {{ __('Switzerland') }}, {{ __('United Kingdom') }}, {{ __('United States') }}, {{ __('Canada') }}, {{ __('Australia') }}, {{ __('New Zealand') }}, {{ __('France') }}, {{ __('Spain') }}, {{ __('Italy') }}, {{ __('Netherlands') }}, {{ __('Belgium') }}, {{ __('Luxembourg') }} -->
        <select id="country" name="country" class="mt-1 block w-full border-gray-300 focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 rounded-md shadow-sm" autocomplete="country-name" v-model="form.country">
          <option v-for="(country,k) in countries" :value="k">{{ __(country) }}</option>
        </select>
        <jet-input-error :message="form.errors.country" class="mt-2"/>
      </div>

      <div class="col-span-6">
        <jet-label for="street-address" :value="__('Street address')"/>
        <jet-input id="street-address" type="text" name="street-address" class="mt-1 block w-full" v-model="form.street"
                   autocomplete="street-address"/>
        <jet-input-error :message="form.errors.street" class="mt-2"/>
      </div>

      <div class="col-span-6 sm:col-span-6 lg:col-span-2">
        <jet-label for="city" :value="__('City')"/>
        <jet-input id="city" type="text" name="city" class="mt-1 block w-full" autocomplete="address-level2" v-model="form.city"/>
        <jet-input-error :message="form.errors.city" class="mt-2"/>
      </div>

      <div class="col-span-6 sm:col-span-3 lg:col-span-2">
        <jet-label for="region" :value="__('State / Province')" />
        <jet-input id="region" type="text" name="region" class="mt-1 block w-full" autocomplete="address-level1" v-model="form.region"/>
        <jet-input-error :message="form.errors.region" class="mt-2" />
      </div>

      <div class="col-span-6 sm:col-span-3 lg:col-span-2">
        <jet-label for="postal-code" :value="__('ZIP / Postal code')"/>
        <jet-input id="postal-code" type="text" name="postal-code" class="mt-1 block w-full" v-model="form.postcode"
                   autocomplete="postal-code"/>
        <jet-input-error :message="form.errors.postcode" class="mt-2"/>
      </div>

    </template>

    <template #actions>
      <jet-action-message :on="form.recentlySuccessful" class="mr-3">
        {{ __('Saved.') }}
      </jet-action-message>

      <jet-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
        {{ __('Save') }}
      </jet-button>
    </template>
  </jet-form-section>
</template>

<script>
import {defineComponent} from 'vue'
import JetButton from '@/Jetstream/Button.vue'
import JetFormSection from '@/Jetstream/FormSection.vue'
import JetInput from '@/Jetstream/Input.vue'
import JetInputError from '@/Jetstream/InputError.vue'
import JetLabel from '@/Jetstream/Label.vue'
import JetActionMessage from '@/Jetstream/ActionMessage.vue'
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue'
import LanguageSelector from "@/Layouts/Misc/LanguageSelector";

export default defineComponent({
  components: {
    LanguageSelector,
    JetActionMessage,
    JetButton,
    JetFormSection,
    JetInput,
    JetInputError,
    JetLabel,
    JetSecondaryButton,
  },

  props: ['user', 'countries'],

  data() {
    return {
      form: this.$inertia.form({
        _method: 'PUT',
        name: this.user.name,
        email: this.user.email,
        photo: null,
        firstname: this.user.firstname,
        lastname: this.user.lastname,
        country: this.user.country,
        street: this.user.street,
        city: this.user.city,
        region: this.user.region,
        postcode: this.user.postcode,
      }),

      photoPreview: null,
    }
  },

  methods: {
    updateProfileInformation() {
      if (this.$refs.photo) {
        this.form.photo = this.$refs.photo.files[0]
      }

      this.form.post(route('user-profile-information.update'), {
        errorBag: 'updateProfileInformation',
        preserveScroll: true,
        onSuccess: () => (this.clearPhotoFileInput()),
      });
    },

    selectNewPhoto() {
      this.$refs.photo.click();
    },

    updatePhotoPreview() {
      const photo = this.$refs.photo.files[0];

      if (!photo) return;

      const reader = new FileReader();

      reader.onload = (e) => {
        this.photoPreview = e.target.result;
      };

      reader.readAsDataURL(photo);
    },

    deletePhoto() {
      this.$inertia.delete(route('current-user-photo.destroy'), {
        preserveScroll: true,
        onSuccess: () => {
          this.photoPreview = null;
          this.clearPhotoFileInput();
        },
      });
    },

    clearPhotoFileInput() {
      if (this.$refs.photo?.value) {
        this.$refs.photo.value = null;
      }
    },
  },
})
</script>
